import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Package,
  Search,
  Filter,
  Download,
  Plus,
  Eye,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';

export default function InventoryManagementPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Mock inventory data
  const inventory = [
    {
      id: 1,
      batchNumber: 'BT-45891',
      productName: 'Blue Dream',
      category: 'Flower',
      strain: 'Hybrid',
      quantity: 1250,
      unit: 'grams',
      thcContent: '22.5%',
      cbdContent: '0.8%',
      location: 'Vault A-12',
      status: 'In Stock',
      lastUpdated: '2024-12-03',
      expiryDate: '2025-06-15',
      supplier: 'Green Valley Farms',
      coaStatus: 'Approved'
    },
    {
      id: 2,
      batchNumber: 'BT-45823',
      productName: 'Sour Diesel Gummies',
      category: 'Edibles',
      strain: 'N/A',
      quantity: 450,
      unit: 'units',
      thcContent: '10mg per unit',
      cbdContent: '0mg',
      location: 'Vault B-05',
      status: 'Low Stock',
      lastUpdated: '2024-12-04',
      expiryDate: '2025-03-20',
      supplier: 'Edible Delights Co',
      coaStatus: 'Pending'
    },
    {
      id: 3,
      batchNumber: 'BT-45734',
      productName: 'OG Kush Concentrate',
      category: 'Concentrates',
      strain: 'Indica',
      quantity: 2800,
      unit: 'grams',
      thcContent: '85.3%',
      cbdContent: '1.2%',
      location: 'Vault A-08',
      status: 'In Stock',
      lastUpdated: '2024-12-02',
      expiryDate: '2025-08-10',
      supplier: 'Pure Extracts LLC',
      coaStatus: 'Approved'
    },
    {
      id: 4,
      batchNumber: 'BT-45912',
      productName: 'Lemon Haze Pre-Rolls',
      category: 'Pre-Rolls',
      strain: 'Sativa',
      quantity: 85,
      unit: 'units',
      thcContent: '19.8%',
      cbdContent: '0.5%',
      location: 'Vault C-03',
      status: 'Critical',
      lastUpdated: '2024-12-05',
      expiryDate: '2025-04-30',
      supplier: 'Green Valley Farms',
      coaStatus: 'Approved'
    },
    {
      id: 5,
      batchNumber: 'BT-45689',
      productName: 'CBD Tincture',
      category: 'Tinctures',
      strain: 'N/A',
      quantity: 320,
      unit: 'bottles',
      thcContent: '0.3%',
      cbdContent: '1000mg per bottle',
      location: 'Vault B-12',
      status: 'In Stock',
      lastUpdated: '2024-11-30',
      expiryDate: '2026-01-15',
      supplier: 'Pure Extracts LLC',
      coaStatus: 'Approved'
    },
    {
      id: 6,
      batchNumber: 'BT-45801',
      productName: 'Purple Kush',
      category: 'Flower',
      strain: 'Indica',
      quantity: 3200,
      unit: 'grams',
      thcContent: '24.1%',
      cbdContent: '0.6%',
      location: 'Vault A-15',
      status: 'In Stock',
      lastUpdated: '2024-12-01',
      expiryDate: '2025-05-20',
      supplier: 'Mountain View Grow',
      coaStatus: 'Approved'
    },
    {
      id: 7,
      batchNumber: 'BT-45745',
      productName: 'Vape Cartridge - Hybrid',
      category: 'Vapes',
      strain: 'Hybrid',
      quantity: 180,
      unit: 'units',
      thcContent: '78.5%',
      cbdContent: '1.8%',
      location: 'Vault C-07',
      status: 'Low Stock',
      lastUpdated: '2024-12-04',
      expiryDate: '2025-07-15',
      supplier: 'Pure Extracts LLC',
      coaStatus: 'Approved'
    },
    {
      id: 8,
      batchNumber: 'BT-45623',
      productName: 'Chocolate Bar - 100mg',
      category: 'Edibles',
      strain: 'N/A',
      quantity: 520,
      unit: 'units',
      thcContent: '100mg per bar',
      cbdContent: '0mg',
      location: 'Vault B-09',
      status: 'In Stock',
      lastUpdated: '2024-12-03',
      expiryDate: '2025-02-28',
      supplier: 'Edible Delights Co',
      coaStatus: 'Approved'
    }
  ];

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = 
      item.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || item.category.toLowerCase() === categoryFilter.toLowerCase();
    
    return matchesSearch && matchesCategory;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'In Stock':
        return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="h-3 w-3" />In Stock</Badge>;
      case 'Low Stock':
        return <Badge className="bg-orange-500 text-white flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Low Stock</Badge>;
      case 'Critical':
        return <Badge variant="destructive" className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Critical</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const summaryCards = [
    {
      title: 'Total SKUs',
      value: inventory.length,
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      change: '+12',
      changeType: 'positive'
    },
    {
      title: 'In Stock',
      value: inventory.filter(i => i.status === 'In Stock').length,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      change: '+3',
      changeType: 'positive'
    },
    {
      title: 'Low Stock',
      value: inventory.filter(i => i.status === 'Low Stock').length,
      icon: AlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      change: '+1',
      changeType: 'negative'
    },
    {
      title: 'Critical',
      value: inventory.filter(i => i.status === 'Critical').length,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      change: '+1',
      changeType: 'negative'
    },
  ];

  const viewItemDetails = (item) => {
    setSelectedItem(item);
    setIsDialogOpen(true);
  };

  const handleExport = () => {
    toast.success('Exporting inventory data to Excel...');
  };

  const handleTransfer = () => {
    toast.success('Opening inventory transfer form...');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Inventory Management</h1>
            <p className="text-muted-foreground mt-1">Seed-to-sale tracking and inventory control</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex items-center gap-2" onClick={handleTransfer}>
              <ArrowRightLeft className="h-4 w-4" />
              Transfer
            </Button>
            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Item
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summaryCards.map((card, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="text-3xl font-bold mt-2">{card.value}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {card.changeType === 'positive' ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      )}
                      <span className={`text-xs ${card.changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
                        {card.change} this month
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 ${card.bgColor} rounded-lg`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Inventory Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Current Inventory</CardTitle>
                <CardDescription>Track all products from seed to sale</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by batch, product, or supplier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="flower">Flower</SelectItem>
                  <SelectItem value="edibles">Edibles</SelectItem>
                  <SelectItem value="concentrates">Concentrates</SelectItem>
                  <SelectItem value="pre-rolls">Pre-Rolls</SelectItem>
                  <SelectItem value="vapes">Vapes</SelectItem>
                  <SelectItem value="tinctures">Tinctures</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch Number</TableHead>
                    <TableHead>Product Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>THC</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>COA</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInventory.length > 0 ? (
                    filteredInventory.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.batchNumber}</TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>
                          <span className={item.status === 'Critical' ? 'text-red-600 font-medium' : ''}>
                            {item.quantity} {item.unit}
                          </span>
                        </TableCell>
                        <TableCell>{item.thcContent}</TableCell>
                        <TableCell>{item.location}</TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                        <TableCell>
                          <Badge variant={item.coaStatus === 'Approved' ? 'success' : 'default'}>
                            {item.coaStatus}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewItemDetails(item)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground">
                        No inventory items found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Item Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Inventory Item Details</DialogTitle>
            <DialogDescription>Complete information about the inventory item</DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Batch Number</p>
                  <p className="font-medium">{selectedItem.batchNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Product Name</p>
                  <p className="font-medium">{selectedItem.productName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Category</p>
                  <p className="font-medium">{selectedItem.category}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Strain</p>
                  <p className="font-medium">{selectedItem.strain}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium">{selectedItem.quantity} {selectedItem.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedItem.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">THC Content</p>
                  <p className="font-medium">{selectedItem.thcContent}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">CBD Content</p>
                  <p className="font-medium">{selectedItem.cbdContent}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium">{selectedItem.location}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Supplier</p>
                  <p className="font-medium">{selectedItem.supplier}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <p className="font-medium">{selectedItem.lastUpdated}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expiry Date</p>
                  <p className="font-medium">{selectedItem.expiryDate}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">COA Status</p>
                  <Badge variant={selectedItem.coaStatus === 'Approved' ? 'success' : 'default'}>
                    {selectedItem.coaStatus}
                  </Badge>
                </div>
              </div>

              <div className="flex gap-2">
                <Button className="flex-1">Update Quantity</Button>
                <Button variant="outline" className="flex-1">Transfer Item</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
